import { UserGender } from '@/core/domain/resources/enums/UserGender.ts'
import type UserPreference from '@/core/domain/users/UserPreference.ts'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'

// Projections par Views pour les utilisateurs
export type UserPublicView = {
  identifier: string
  firstname: string
  lastname: string
  picture: string | null
  roles: UserRole.Values[]
  fullName: string
}
export type UserStaffView = UserPublicView & {
  username: string
  email: string
  gender: UserGender.Values
  createdAt: string
  modifiedAt: string
}
export type UserSelfView = UserStaffView & {
  birthdate: string | null
  preferences: UserPreference
  hasAnsweredTrackingConsentBanner: boolean
  hasConsentedForTracking: boolean
  isRegisterProcessDone: boolean
}
export type UserAdminView = UserSelfView & {
  blocked: boolean
  emailVerified: boolean
  lastLogin: string | null
  loginCount: number | null
}

// Union des types de projections par View
export type UserView = UserPublicView | UserStaffView | UserSelfView | UserAdminView

// Littéraux pour les Views
export const VIEWS = ['public', 'staff', 'self', 'admin'] as const
export type View = (typeof VIEWS)[number]

/**
 * Type d'entrée pour le constructeur LocalUser
 */
export interface LocalUserProps {
  identifier: string
  firstname: string
  lastname: string
  picture: string | null
  roles: UserRole.Values[]
  username?: string
  email?: string
  gender?: UserGender.Values
  createdAt?: string | null
  modifiedAt?: string | null
  birthdate?: string | null
  preferences?: UserPreference
  hasAnsweredTrackingConsentBanner?: boolean
  hasConsentedForTracking?: boolean
  isRegisterProcessDone?: boolean
  blocked?: boolean
  emailVerified?: boolean
  lastLogin?: string | null
  loginCount?: number | null
}

/**
 * Implémentation du type LocalUserEntity
 */
export class LocalUser {
  readonly identifier: string
  readonly firstname: string
  readonly lastname: string
  readonly username?: string
  readonly email?: string
  readonly birthdate?: string | null
  readonly gender?: UserGender.Values
  readonly roles: UserRole.Values[]
  readonly picture: string | null
  readonly blocked?: boolean
  readonly emailVerified?: boolean
  readonly lastLogin?: string | null
  readonly loginCount?: number | null
  readonly preferences?: UserPreference
  readonly hasAnsweredTrackingConsentBanner?: boolean
  readonly hasConsentedForTracking?: boolean
  readonly isRegisterProcessDone?: boolean
  readonly createdAt?: string | null
  readonly modifiedAt?: string | null

  constructor(user: LocalUserProps, view?: View) {
    this.identifier = user.identifier
    this.firstname = user.firstname
    this.lastname = user.lastname
    this.username = user.username
    this.email = user.email
    this.birthdate = user.birthdate ?? null
    this.gender = user.gender
    this.roles = user.roles
    this.picture = user.picture
    this.blocked = user.blocked
    this.emailVerified = user.emailVerified
    this.lastLogin = user.lastLogin
    this.loginCount = user.loginCount
    this.preferences = user.preferences
    this.hasAnsweredTrackingConsentBanner = user.hasAnsweredTrackingConsentBanner
    this.hasConsentedForTracking = user.hasConsentedForTracking
    this.isRegisterProcessDone = user.isRegisterProcessDone
    this.createdAt = user.createdAt ?? null
    this.modifiedAt = user.modifiedAt ?? null

    // On définit une propriété non sérializable
    Object.defineProperty(this, 'effectiveView', {
      value: view,
      writable: false,
      configurable: false,
      enumerable: false
    })
  }

  /**
   * Retourne la vue actuelle de l'instance
   * @returns
   */
  get effectiveView(): View {
    return (this as any).effectiveView
  }

  /**
   * Retourne le nom complet de l'utilisateur
   */
  get fullName(): string {
    return `${this.firstname} ${this.lastname}`
  }

  /**
   * Vérifie si l'utilisateur a un rôle spécifique
   * @param role Rôle à vérifier
   * @returns Vrai si l'utilisateur a le rôle spécifié
   */
  hasRole(role: UserRole.Values): boolean {
    return this.roles.includes(role)
  }
}

/**
 * Détermine si un objet est un UserStaffView
 * @param data
 */
export function isUserStaffView(data: LocalUser): data is LocalUser & UserStaffView {
  return (
    data.effectiveView === 'staff' ||
    data.effectiveView === 'self' ||
    data.effectiveView === 'admin'
  )
}

/**
 * Détermine si un objet est un UserSelfView
 * @param data
 */
export function isUserSelfView(data: LocalUser): data is LocalUser & UserSelfView {
  return data.effectiveView === 'self'
}

/**
 * Détermine si un objet est un UserAdminView
 * @param data
 */
export function isUserAdminView(data: LocalUser): data is LocalUser & UserAdminView {
  return data.effectiveView === 'admin'
}
